import React, { useEffect } from "react";
import { Button, MenuItem } from "@material-ui/core";
import { Stripe } from "@stripe/stripe-js";

import { formatFullLocation, formatPrice } from "utils";
import { SavedCard, StripeCard, Token } from "../../types";
import { tokensToBuy } from "../../config";

import { Location } from 'components/BillingLocationSelector'

import {
    InputContainer,
    StyledMaterialSelector,
    PriceDetail,
    TotalPriceDetail,
    SectionTitle,
    CreditCardInfoContainer,
    CreditCardInfoTitle,
    CreditCardInfo,
    CreditCardNumber,
    UpdateLink,
    UpdateLocationLink,
    StyledLockIcon,
    InfoMessageContainer,
    InfoMessage,
    ErrorMessage
} from "./Checkout.styles";
import {
    PopupContainer,
    PopupTitle,
    PopupTitleContainer,
    StyledCrossButtonDark,
    ButtonContainer
} from "../../common.styles";

interface CheckoutProps {
    stripe: Stripe | null,
    onCloseButtonClick: () => void,
    country: string,
    disabled: boolean,
    errorMessage?: string,
    locations: Location[],
    selectedLocation: Location | null,
    onSelectedLocationChange: (value: Location) => void,
    onTokenSelect: (token: Token) => void
    selectedToken: Token,
    savedBillingCard: SavedCard | null,
    setSavedBillingCard: (card: SavedCard | null) => void
    updatedBillingStripeCard: StripeCard | null,
    setUpdatedBillingStripeCard: (card: StripeCard | null) => void,
    onSubmit: () => void,
    onAddLocationClick: () => void
}

const Checkout = ({
    stripe,
    onCloseButtonClick,
    selectedToken,
    country,
    onTokenSelect,
    locations,
    selectedLocation,
    savedBillingCard,
    setSavedBillingCard,
    updatedBillingStripeCard,
    setUpdatedBillingStripeCard,
    onSelectedLocationChange,
    onSubmit,
    disabled,
    errorMessage,
    onAddLocationClick
}: CheckoutProps) => {
    useEffect(() => {
        if (stripe && !savedBillingCard && !updatedBillingStripeCard) {
            const cardElement = stripe.elements().create('card')
            cardElement.mount('#card-element');
            setUpdatedBillingStripeCard(cardElement)
        }
    }, [stripe, savedBillingCard, updatedBillingStripeCard]);

    const onUpdateBillingCardClick = () => {
        setSavedBillingCard(null);
    }

    const { price, discount, id } = selectedToken;
    const discountValue = discount ? Math.round(price * (discount || 0) / 100) : 0;
    const taxesValue = country === 'CA' ? (price - discountValue) * 0.13 : 0;
    const totalPriceValue = price - discountValue + taxesValue;

    return (
        <PopupContainer>
            <PopupTitleContainer>
                <PopupTitle>Checkout</PopupTitle>
                <StyledCrossButtonDark className="no-fastclick" onClick={onCloseButtonClick}/>
            </PopupTitleContainer>
            <InputContainer>
                {selectedToken ? <StyledMaterialSelector
                    id="token"
                    value={id}
                    name="token"
                    label="Tokens"
                    onChange={(event) => onTokenSelect(tokensToBuy.find(({id}) => id === Number(event.target.value)) as Token)}
                >
                    {tokensToBuy.map(({id, count}) => (
                        <MenuItem key={id} value={id}>
                            {count} Resumé {count <= 1 ? 'Token' : 'Tokens'}
                        </MenuItem>
                    ))}
                </StyledMaterialSelector> : null}
            </InputContainer>
            <PriceDetail>
                <div>Subtotal</div>
                <div>{formatPrice(price)}</div>
            </PriceDetail>
            {discount ? (
                <PriceDetail>
                    <div>Save {discount}%</div>
                    <div>{formatPrice(-1 * discountValue)}</div>
                </PriceDetail>
            ): null}
            {country === 'CA' && taxesValue ? (
                <PriceDetail>
                    <div>HST Taxes</div>
                    <div>{formatPrice(taxesValue)}</div>
                </PriceDetail>
            ) : null}
            <TotalPriceDetail>
                <div>Total</div>
                <div>{formatPrice(totalPriceValue)}</div>
            </TotalPriceDetail>
            <SectionTitle>
                Payment details
            </SectionTitle>
            <InputContainer>
                <StyledMaterialSelector
                    id="locations"
                    value={selectedLocation?.id}
                    name="locations"
                    label="Billing Location"
                    onChange={(event) => onSelectedLocationChange(locations.find(({id}) => id === Number(event.target.value)) as Location)}
                >
                    {locations.map((location) => <MenuItem key={location.id}
                                                           value={location.id}>{formatFullLocation(location)}</MenuItem>)}
                </StyledMaterialSelector>
                <UpdateLocationLink className="no-fastclick" onClick={onAddLocationClick}>Add New Location</UpdateLocationLink>
            </InputContainer>
            {!savedBillingCard ? (
                <CreditCardInfoContainer>
                    <CreditCardInfoTitle>Update Payment Card</CreditCardInfoTitle>
                    <div id="card-element" />
                </CreditCardInfoContainer>
            ) :
                <CreditCardInfoContainer>
                    <CreditCardInfoTitle>Saved Card</CreditCardInfoTitle>
                    <CreditCardInfo>
                        <CreditCardNumber>
                            ···· ···· ···· {savedBillingCard.last4}
                        </CreditCardNumber>
                        <UpdateLink className="no-fastclick" onClick={onUpdateBillingCardClick}>Update Payment Card</UpdateLink>
                    </CreditCardInfo>
                </CreditCardInfoContainer>
            }
            <ButtonContainer>
                {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    color="primary"
                    size="large"
                    id="continue-button"
                    disabled={disabled}
                    className="bigButtonInNewStyle"
                >
                    Pay {formatPrice(totalPriceValue)}
                </Button>
            </ButtonContainer>
            <InfoMessageContainer>
                <StyledLockIcon />
                <InfoMessage>Safe & Secure</InfoMessage>
            </InfoMessageContainer>
        </PopupContainer>
    );
}

export default Checkout;
